/** Links from the Overzicht into the lists, with the filter in `f` (design §5.5; `f` replaces the view's filter). */
import { encodeFilter } from '../query/codec'
import type { Condition } from '../query/types'
import type { Scope } from './types'

const ACTIVE: Condition = { property: 'status', operator: 'is_any_of', value: ['actief'] }
const mineCondition = (scope: Scope): Condition[] => (scope === 'mine' ? [{ property: 'owner', operator: 'is_any_of', value: ['me'] }] : [])

function link(path: string, view: string | null, conditions: Condition[] | null): string {
  const params = new URLSearchParams()
  if (view) params.set('view', view)
  if (conditions) params.set('f', encodeFilter({ version: 2, quick: conditions, groups: [] }))
  const query = params.toString()
  return query ? `${path}?${query}` : path
}

/** Te lang stil: the standard view, with "Eigenaar: Ik" added under Mijn. */
export function staleHref(scope: Scope): string {
  const mine = mineCondition(scope)
  const quiet: Condition = { property: 'last_contact_at', operator: 'not_in_last_days', value: 90 }
  return link('/relaties/contacten', 'sys:stil', mine.length > 0 ? [ACTIVE, quiet, ...mine] : null)
}

export function relationsHref(scope: Scope): string {
  const mine = mineCondition(scope)
  return link('/relaties/contacten', 'sys:alle', mine.length > 0 ? [ACTIVE, ...mine] : null)
}

/** Afspraken: Actief (Mijn afspraken under Mijn) or Achterstallig, with "Eigenaar: Ik" added under Mijn. */
export function agreementsHref(scope: Scope, view: 'sys:alle' | 'sys:achterstallig' | 'sys:komend' = 'sys:alle'): string {
  if (scope === 'team') return link('/relaties/afspraken', view, null)
  if (view === 'sys:alle') return link('/relaties/afspraken', 'sys:mijn', null)
  const conditions: Condition[] =
    view === 'sys:achterstallig'
      ? [{ property: 'overdue', operator: 'is_true' }]
      : [ACTIVE, { property: 'next_due_on', operator: 'in_next_days', value: 30 }]
  return link('/relaties/afspraken', view, [...conditions, ...mineCondition(scope)])
}

/** Contacten filtered on a rhythm state. */
export function rhythmHref(status: 'gemist' | 'binnenkort' | 'op_schema', scope: Scope): string {
  return link('/relaties/contacten', null, [ACTIVE, { property: 'rhythm_status', operator: 'is_any_of', value: [status] }, ...mineCondition(scope)])
}

/** Contacten or Organisaties filtered on a category (or on none). */
export function categoryHref(object: 'contacten' | 'organisaties', categoryId: string | null, scope: Scope): string {
  const category: Condition = categoryId
    ? { property: 'category', operator: 'is_any_of', value: [categoryId] }
    : { property: 'category', operator: 'is_unknown' }
  return link(`/relaties/${object}`, null, [ACTIVE, category, ...mineCondition(scope)])
}

export function relationHref(type: 'contact' | 'organisation', id: string, tab: string | null = 'activiteit'): string {
  const base = type === 'contact' ? `/relaties/contacten/${id}` : `/relaties/organisaties/${id}`
  return tab ? `${base}?tab=${tab}` : base
}
